import React, { useCallback, useState } from 'react';

import { apiPatchAsUser } from '../../../lib/api_v2';
import db from '../../../lib/db';
import { logError } from '../../../lib/logger';
import { currentUserId } from '../../../lib/meta';
import { cn } from '../../../lib/utils';

import { FIELD_INPUT_CLASS, FIELD_INPUT_ERROR_CLASS, FIELD_LABEL_CLASS, FIELD_SUBMIT_BUTTON_CLASS, firstFieldError, responseValidations } from './accountStyles';

const AccountEmailPanel = ({ userEmail }) => {
  const initialEmail = userEmail || '';
  const [email, setEmail] = useState(initialEmail);
  const [currentPassword, setCurrentPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const trimmedEmail = email.trim();
  const hasChanges = trimmedEmail.length > 0 && trimmedEmail !== initialEmail.trim();
  const isDisabled = isSaving || !hasChanges || currentPassword.length === 0;
  const emailError = firstFieldError(errors.email);
  const currentPasswordError = firstFieldError(errors.current_password);

  const handleEmailChange = useCallback(function handleAccountEmailChange(event) {
    setEmail(event.target.value);
    setStatus(null);
    setErrors((prev) => ({ ...prev, email: null, general: null }));
  }, []);

  const handleCurrentPasswordChange = useCallback(function handleAccountEmailCurrentPasswordChange(event) {
    setCurrentPassword(event.target.value);
    setStatus(null);
    setErrors((prev) => ({ ...prev, current_password: null, general: null }));
  }, []);

  const handleSubmit = useCallback(
    async function submitAccountEmail(event) {
      event.preventDefault();
      if (isSaving || !hasChanges || currentPassword.length === 0) return;

      setIsSaving(true);
      setStatus(null);
      setErrors({});

      try {
        const response = await apiPatchAsUser('/account/email', {
          user: { email: trimmedEmail, current_password: currentPassword },
        });
        const data = await response.json();
        const updatedUser = data?.data?.user;

        if (!response.ok || data.ok !== true || !updatedUser) {
          setErrors(responseValidations(data) || { general: 'Unable to update email.' });
          return;
        }

        const userId = currentUserId();
        if (userId) await db.users.update(String(userId), updatedUser);
        setCurrentPassword('');
        setStatus('Saved');
      } catch (saveError) {
        logError('AccountEmailPanel', 'Failed to update email', { error: saveError });
        setErrors({ general: 'Unable to update email.' });
      } finally {
        setIsSaving(false);
      }
    },
    [currentPassword, hasChanges, isSaving, trimmedEmail],
  );

  return (
    <form onSubmit={handleSubmit} className="w-full">
      {errors.general && (
        <div className="mb-5 rounded-md bg-red-50 p-4 text-sm text-red-800 dark:bg-red-950 dark:text-red-200">{errors.general}</div>
      )}

      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <label htmlFor="account_email" className={FIELD_LABEL_CLASS}>
            Email
          </label>
          <input
            id="account_email"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={handleEmailChange}
            placeholder="you@example.com"
            className={cn(FIELD_INPUT_CLASS, emailError && FIELD_INPUT_ERROR_CLASS)}
          />
          {emailError && <span className="text-xs text-red-400">{emailError}</span>}
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="account_email_current_password" className={FIELD_LABEL_CLASS}>
            Current password
          </label>
          <input
            id="account_email_current_password"
            name="current_password"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={handleCurrentPasswordChange}
            placeholder="Enter your password to confirm"
            className={cn(FIELD_INPUT_CLASS, currentPasswordError && FIELD_INPUT_ERROR_CLASS)}
          />
          {currentPasswordError && <span className="text-xs text-red-400">{currentPasswordError}</span>}
        </div>

        <div className="flex items-center justify-end gap-3">
          <span className="min-h-5 text-xs text-gray-500 dark:text-neutral-400" aria-live="polite">
            {isSaving ? 'Saving...' : status}
          </span>
          <button type="submit" className={FIELD_SUBMIT_BUTTON_CLASS} disabled={isDisabled}>
            Update
          </button>
        </div>
      </div>
    </form>
  );
};

export default AccountEmailPanel;
